// src/components/support/forms/ChangeProgram.js
import React, { useState } from "react";
import { ChevronLeft } from "lucide-react";
import moment from "moment";
import { useRequestProcessor } from "../../../requestProcessor";
import SpinnerLoading from "../../common/SpinnerLoading";
import FootprintDatePicker from "../../common/FootprintDatePicker";
import { useAtom } from "jotai";
import { activeChildDetail, appConstants, childList } from "../../../atomstore/store";
import {
    fetchCenterProgramTimeSlots,
    fetchCenters,
    fetchProgramList,
    submitProgramChange,
} from "../../../queryfunctions/queryfunctions";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../../common/CustomSelect";

const ChangeProgram = () => {
    const navigate = useNavigate();
    const { useQueryWrapper, useMutationWrapper } = useRequestProcessor();
    const [children] = useAtom(childList);
    const [constants] = useAtom(appConstants);

    const firstNonPausedIndex = children.findIndex(c => c?.pause_state !== 1);
    const [selectedChild, setSelectedChild] = useState(firstNonPausedIndex >= 0 ? firstNonPausedIndex : 0);
    const [selectedProgram, setSelectedProgram] = useState("");
    const [selectedDate, setSelectedDate] = useState("");
    const [programs, setProgram] = useState();
    const [error, setError] = useState("");
    const today = moment();
    const isCurrentMonthWindow = today.date() < 5;
    const currentMonthStart = today.clone().startOf("month").date(1);
    const currentMonthEnd = currentMonthStart.clone().date(5);
    const isWeekend = (date) => date.day() === 0 || date.day() === 6;
    // If month 1st is weekend, shift to the next weekday (Mon)
    const getFirstWeekdayOfMonth = (monthMoment) => {
        const first = monthMoment.clone().startOf("month").date(1);
        while (isWeekend(first)) {
            first.add(1, "day");
        }
        return first;
    };
    const firstAllowedDate = isCurrentMonthWindow
        ? currentMonthStart.clone()
        : getFirstWeekdayOfMonth(today.clone().add(1, "month"));
    const isAllowedEffectiveDate = (date) => {
        const mDate = moment(date);
        const selectedMonthStart = mDate.clone().startOf("month");
        const isSelectedCurrentMonth = selectedMonthStart.isSame(currentMonthStart, "month");
        const isSelectedFutureMonth = selectedMonthStart.isAfter(currentMonthStart, "month");

        if (isSelectedCurrentMonth) {
            return (
                isCurrentMonthWindow &&
                mDate.isBetween(currentMonthStart, currentMonthEnd, "day", "[]")
            );
        }

        if (isSelectedFutureMonth) {
            return mDate.isSame(getFirstWeekdayOfMonth(selectedMonthStart), "day");
        }

        return false;
    };



    const {
        data: fetched_program_slots,
        isLoading,
    } = useQueryWrapper(
        ["program_slots", children[selectedChild]?.id],
        () => fetchCenterProgramTimeSlots(children[selectedChild]),
        {
            enabled: !!children[selectedChild]?.id,
            onSuccess: (data) => {
                // Example: set first program by default
                if (data && data.length > 0) {
                    setProgram(data);
                }
            },
        }
    );

    console.log("Selected program", selectedProgram)


    const changeProgram = useMutationWrapper(
        ["changeProgram"],
        () => {
            console.log("[Change Program] API Request:", {
                child: children[selectedChild],
                selectedDate,
                selectedProgram,
            });
            return submitProgramChange(
                selectedDate,
                selectedProgram,
                children[selectedChild]
            );
        },
        {
            onSuccess: (data) => {
                console.log("[Change Program] API Success:", data);
            },
        }
    );

    const validate = () => {
        if (!selectedProgram) {
            setError("Please select a new program");
            return false;
        } else if (!selectedDate) {
            setError("Please select effective from date");
            return false;
        } else {
            const effDate = moment(selectedDate);
            const selectedMonthStart = effDate.clone().startOf("month");
            const isSelectedCurrentMonth = selectedMonthStart.isSame(currentMonthStart, "month");
            const isSelectedFutureMonth = selectedMonthStart.isAfter(currentMonthStart, "month");

            if (isSelectedCurrentMonth) {
                if (!isCurrentMonthWindow || !effDate.isBetween(currentMonthStart, currentMonthEnd, "day", "[]")) {
                    setError(
                        isCurrentMonthWindow
                            ? `Effective date must be between ${currentMonthStart.format(
                                "MMM DD"
                            )} and ${currentMonthEnd.format("MMM DD")}`
                            : "Please select 1st of any future month"
                    );
                    return false;
                }
            } else if (isSelectedFutureMonth) {
                const allowedFutureDate = getFirstWeekdayOfMonth(selectedMonthStart);
                if (!effDate.isSame(allowedFutureDate, "day")) {
                    setError(
                        `For future months, effective date must be ${allowedFutureDate.format("MMM DD")}`
                    );
                    return false;
                }
            } else {
                setError("Please select a valid effective date");
                return false;
            }
        }
        setError("");
        return true;
    };

    const submitRequest = () => {
        if (validate()) {
            changeProgram.mutate();
        }
    };
    console.log("programs", programs)

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {changeProgram.isLoading && <SpinnerLoading />}

            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Change Program</h2>
            </div>

            {/* Form Content */}
            <div className="p-4 space-y-4">
                {children.filter(c => c?.pause_state !== 1).length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Child
                        </label>
                        <CustomSelect
                            label="Select Child"
                            value={selectedChild}
                            onChange={(val) => setSelectedChild(Number(val))}
                            options={children
                                .map((child, idx) => ({ value: idx, label: `${child?.name} ${child?.last_name}` }))
                                .filter(opt => children[opt.value]?.pause_state !== 1)
                            }
                        />
                    </div>
                )}

                {/* Current Program Info */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                        {(constants?.changeProgramInfoBanner ||
                            "Current Program for {{name}} {{lastName}}: {{currentProgram}}")
                            .replace(
                                "{{name}}",
                                children[selectedChild]?.name?.split(" ")?.[0] || ""
                            )
                            .replace("{{lastName}}", children[selectedChild]?.last_name || "")
                            .replace(
                                "{{currentProgram}}",
                                children[selectedChild]?.length
                                    ? `${children[selectedChild]?.length}(${children[selectedChild]?.program_time_slots || ""})`
                                    : "N/A"
                            )}
                    </p>
                </div>

                {/* New Program Select */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Program
                    </label>
                    <CustomSelect
                        label="New Program"
                        value={selectedProgram?.time_slot_program_name || ""}
                        onChange={(value) => {
                            setError(undefined);

                            // value === opt.value === time_slot_program_name
                            const selectedObj = programs.find(
                                (p) => p.time_slot_program_name === value
                            );

                            setSelectedProgram(selectedObj);
                        }}
                        options={programs
                            ?.filter(
                                (item) =>
                                    item?.time_slot_program_name !==
                                    children[selectedChild]?.["length"]
                            )
                            .map((p) => ({ value: p?.time_slot_program_name, label: p?.time_slot_display_name }))}
                        placeholder="Select program"
                    />

                </div>

                {/* Effective From Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Effective From
                    </label>
                    <FootprintDatePicker
                        onChange={(date) => {
                            setSelectedDate(moment(date));
                            setError("");
                        }}
                        disableWeekends={true}
                        minimumDate={firstAllowedDate}
                        maximumDate={null}
                        customIsValidDate={isAllowedEffectiveDate}
                        placeholder="Select Date"
                        inputProps={{
                            placeholder: "Select Date",
                        }}
                    />
                </div>

                {/* Error Message */}
                {error && <p className="text-red-500 text-sm">{error}</p>}

                {/* Submit */}
                <button
                    onClick={submitRequest}
                    disabled={changeProgram.isLoading}
                    className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold hover:bg-cyan-600 transition disabled:opacity-60"
                >
                    Submit Request
                </button>
            </div>
        </div>
    );
};

export default ChangeProgram;
